import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/model/auth-model/auth.service';
import { ToastrService } from 'ngx-toastr';
import SystemConstants from 'src/app/util/constants/SystemConstants';
import { UserModelService } from 'src/app/model/user-model/user-model.service';
import { IGetUser } from 'src/app/interface/user/i-get-user';
import { ImageLoaderModule } from 'src/app/shared/modules/image-loader/image-loader.module';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SharedModule } from 'src/app/shared/shared.module';
import { Translations } from 'src/app/util/service/internationalization/translations.service';
import { UserNotificationsService } from '../../shared/services/user-notifications.service';
import { INotificationList } from 'src/app/interface/notification/i-notfication';
import { Globals, UserRole } from 'src/app/globals/globals';
import { CustomConfirmationPopupService } from '../../shared/components/custom-confirmation-popup-component/custom-confirmation-popup.service';
import { dateFilterType } from '../../interface/i-signup';
import { Subscription } from 'rxjs';
import { UserHeaderUiStateService } from 'src/app/user/shared/services/user-header-ui-state.service';


@Component({
  selector: 'app-user-header', 
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ImageLoaderModule,
    RouterLink,
    RouterLinkActive,
    SharedModule,
  ],
  templateUrl: '../../view/user-header/user-header.component.html',
  styleUrls: ['../../view/user-header/user-header.component.scss'],
})
export class UserHeaderComponent implements OnInit, OnDestroy {
  readonly systemConstants = SystemConstants;
  loginUser: IGetUser | null = null;
  isNotificationsPage = false;
  userRole = UserRole;

  private memberId: string | null = null;

  notificationList: INotificationList[] = [];
  isNotificationsLoading = false;
  isRemovingNotification = false;
  unreadNotificationCount = 0;

  selectedNotificationDateFilter:dateFilterType = 'all';
  isNotificationDateFilterDropdownOpen = false;

  private readonly NOTIFICATION_LIMIT = 20;
  private readonly NOTIFICATION_DELETE_STATUS = 2;

  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  showMasqueradeBanner = false;
  showAddChallengeHeader = false;
  private addChallengeHeaderSub: Subscription | null = null;
  private selectedChallengesCountSub: Subscription | null = null;
  private routerEvents: Subscription | null = null;
  selectedChallengesCount = 0;

  constructor(
    private authService: AuthService,
    private toastr: ToastrService,
    private userModelService: UserModelService,
    private readonly userNotificationsService: UserNotificationsService,
    private router: Router,
    private confirmationService: CustomConfirmationPopupService,
    public translations: Translations,
    public globals: Globals,
    private userHeaderUiStateService: UserHeaderUiStateService,
  ) {
    this.routerEvents = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeMobileMenu();
      }
    });
  }

  get isAdminPortalUser(): boolean {
    const r = this.loginUser?.role;
    return (
      r === UserRole.SUPER_ADMIN || r === UserRole.LOCALE_ADMIN
    );
  }

  toggleMobileMenu(): void {
    this.userHeaderUiStateService.toggleMobileMenu();
  }

  closeMobileMenu(): void {
    this.userHeaderUiStateService.closeMobileMenu();
  }

  onAdminMenuClick(event: Event): void {
    this.closeMobileMenu();
    event.preventDefault();
    const role = this.loginUser?.role;
    if (role !== UserRole.SUPER_ADMIN && role !== UserRole.LOCALE_ADMIN) {
      return;
    }

    this.confirmationService.open({
      title: 'Admin panel',
      message: 'Go to the admin dashboard?',
      type: 'warning',
      yesText: 'Yes',
      noText: 'No',
      onConfirm: () => {
        if (role === UserRole.SUPER_ADMIN) {
          localStorage.setItem('backUrl', '/dashboard');
          void this.router.navigateByUrl('/dashboard');
        } else {
          localStorage.setItem('backUrl', '/locale-admin');
          void this.router.navigateByUrl('/locale-admin');
        }
      },
    });
  }

  ngOnInit(): void {
    this.updateMasqueradeBannerState();
    this.loadUserData();

    // Toggle add-challenge UI in this global header.
    this.addChallengeHeaderSub = this.userHeaderUiStateService.addChallengeActive$.subscribe(
      (isActive) => {
        this.showAddChallengeHeader = isActive;
      }
    );

    this.selectedChallengesCountSub = this.userHeaderUiStateService.selectedChallengesCount$.subscribe(
      (count) => {
        this.selectedChallengesCount = count;
      }
    );
  }

  ngOnDestroy(): void {
    this.addChallengeHeaderSub?.unsubscribe();
    this.selectedChallengesCountSub?.unsubscribe();
    this.routerEvents?.unsubscribe();
  }

  private loadUserData(): void {
    const tokenData = this.authService.getUserData();

    if (tokenData && tokenData.data) {
      const baseUser = tokenData.data;
      this.memberId = baseUser?._id ? String(baseUser._id) : null;

      this.userModelService
        .finduserById({ _id: baseUser._id })
        .subscribe(
          (res) => {
            if (res && res.status === 'OKAY') {
              this.loginUser = res.data?.role != null ? res.data : baseUser;
            } else {
              this.loginUser = baseUser;
            }
          },
          () => {
            this.loginUser = baseUser;
          }
        );

      if (this.memberId) {
        this.loadUnreadNotificationCount();
      }
    }
  }

  private loadUnreadNotificationCount(): void {
    if (!this.memberId) {
      return;
    }

    const payload: any = {
      memberId: this.memberId,
      isNotificationRead: false,
    };

    this.userNotificationsService.getUnreadNotificationCount(payload).subscribe({
      next: (res) => {
        this.unreadNotificationCount = typeof res?.data === 'number' ? res.data : 0;
      },
      error: () => {
        this.unreadNotificationCount = 0;
      },
    });
  }

  getImageSrcByImageType(imageDetails: { imageType: string; image: string }) {
    return this.userModelService.getImageSrcByImageType({
      imageType: imageDetails.imageType as any,
      image: imageDetails.image,
    });
  }
  private syncBodyScrollLock(): void {
    const shouldLock = Boolean(this.isNotificationsPage);
    document.body.classList.toggle('body-scroll-hide', shouldLock);
  }

  isImageCdnByImageType(imageType: string) {
    return this.userModelService.isImageCdnByImageType(imageType as any);
  }

  onLogout(): void {
    this.authService.logout();
    this.toastr.success(SystemConstants.SUCCESS, SystemConstants.LOGOUT_MSG);
  }

  onExitMasqueradeClick(event: Event): void {
    event.preventDefault();
    if (!this.showMasqueradeBanner) {
      return;
    }

    this.confirmationService.open({
      title: 'Exit masquerade',
      message: 'Return to admin dashboard?',
      type: 'warning',
      yesText: 'Yes',
      noText: 'No',
      onConfirm: () => this.exitMasqueradeMode(),
    });
  }

  get masqueradeBannerText(): string {
    const adminName = localStorage.getItem('masqueradeAdminName') || 'Admin';
    const username = localStorage.getItem('masqueradeUsername') || (this.loginUser ? this.getDisplayName(this.loginUser) : 'User');
    return `${adminName} logged in as: ${username}`;
  }
  toggleNotificationsPage(): void {
    const wasOpen = this.isNotificationsPage;
    this.isNotificationsPage = !this.isNotificationsPage;

    if (this.isNotificationsPage) {
      this.loadNotifications();
    } else if (wasOpen) {
      this.markNotificationsAsReadOnClose();
    }

    this.isNotificationDateFilterDropdownOpen = false;
    this.syncBodyScrollLock();
  }

  private markNotificationsAsReadOnClose(): void {
    if (!this.memberId || this.unreadNotificationCount <= 0) {
      return;
    }

    this.userNotificationsService.markAllNotificationsAsRead().subscribe({
      next: () => {
        this.unreadNotificationCount = 0;
        this.notificationList = this.notificationList.map((notification) => ({
          ...notification,
          isNotificationRead: true,
        }));
      },
      error: () => {
        this.loadUnreadNotificationCount();
      },
    });
  }

  toggleNotificationDateFilterDropdown(): void {
    this.isNotificationDateFilterDropdownOpen = !this.isNotificationDateFilterDropdownOpen;
  }

  setNotificationDateFilter(filter: dateFilterType): void {
    this.selectedNotificationDateFilter = filter;

    if (filter === 'custom') {
      this.isNotificationDateFilterDropdownOpen = true;
      this.customStartDate = null;
      this.customEndDate = null;
      return;
    }

    this.isNotificationDateFilterDropdownOpen = false;
    if (this.isNotificationsPage) this.loadNotifications();
  }

  private loadNotifications(): void {
    if (!this.memberId) return;

    const payload: any = {
      memberId: this.memberId,
      limit: this.NOTIFICATION_LIMIT,
      skip: 0,
    };

    const now = new Date();
    const endTimeIso = now.toISOString();
    if (this.selectedNotificationDateFilter === 'all') {
      payload.customDateTimeFilters = { endTime: endTimeIso };
    } else if (this.selectedNotificationDateFilter === 'weeks') {
      payload.customDateTimeFilters = { endTime: endTimeIso, interval: 2, intervalUnit: 2 };
    } else if (this.selectedNotificationDateFilter === 'months') {
      payload.customDateTimeFilters = { endTime: endTimeIso, interval: 3, intervalUnit: 4 };
    } else if (this.selectedNotificationDateFilter === 'months3') {
      payload.customDateTimeFilters = { endTime: endTimeIso, interval: 3, intervalUnit: 6 };
    } else if (this.selectedNotificationDateFilter === 'custom') {
      if (!this.customStartDate || !this.customEndDate) return;

      const start = new Date(this.customStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(this.customEndDate);
      end.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

      payload.customDateTimeFilters = {
        startTime: start.toISOString(),
        endTime: end.toISOString(),
      };
    }

    this.isNotificationsLoading = true;
    this.userNotificationsService.getNotificationList(payload).subscribe({
      next: (res) => {
        if (res && res.status === SystemConstants.OKAY_MSG) {
          this.notificationList = res.data || [];
        } else {
          this.notificationList = res?.data || [];
        }
        this.loadUnreadNotificationCount();
      },
      error: (err) => {
        this.toastr.error(SystemConstants.ERROR, err?.error?.error?.message || err?.message);
      },
      complete: () => {
        this.isNotificationsLoading = false;
      },
    });
  }

  applyCustomDateRange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    this.isNotificationDateFilterDropdownOpen = false;
    this.loadNotifications();
  }

  removeNotification(notificationId: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isRemovingNotification) return;

    const id = notificationId ? String(notificationId) : '';
    if (!id) return;

    this.isRemovingNotification = true;
    this.userNotificationsService
      .removeNotification(id, this.NOTIFICATION_DELETE_STATUS)
      .subscribe({
        next: (res) => {
          if (res && res.status === SystemConstants.OKAY_MSG) {
            this.loadNotifications();
            this.loadUnreadNotificationCount();
          } else {
            this.toastr.error(SystemConstants.ERROR, res?.errMsg);
          }
        },
        error: (err) => {
          this.toastr.error(SystemConstants.ERROR, err?.error?.error?.message || err?.message);
        },
        complete: () => {
          this.isRemovingNotification = false;
        },
      });
  }

  private updateMasqueradeBannerState(): void {
    this.showMasqueradeBanner = this.isMasqueradeActive();
  }

  private isMasqueradeActive(): boolean {
    return (
      localStorage.getItem('masqueradeActive') === 'true' &&
      Boolean(localStorage.getItem('masqueradeAdminToken'))
    );
  }

  private exitMasqueradeMode(): void {
    const adminToken = localStorage.getItem('masqueradeAdminToken');
    const adminRole = Number(localStorage.getItem('masqueradeAdminRole'));
    this.clearMasqueradeStorage();
    this.updateMasqueradeBannerState();

    if (!adminToken) {
      this.authService.logout();
      return;
    }

    this.authService.setUser({ token: adminToken, isRemember: true });
    this.authService.isLoggedInForHeaderSubject.next(false);
    localStorage.setItem('role', String(adminRole));
    localStorage.setItem('isLoggedInSuccessfully', JSON.stringify(true));

    if (adminRole === UserRole.LOCALE_ADMIN) {
      localStorage.setItem('backUrl', '/locale-admin');
      void this.router.navigateByUrl('/locale-admin');
      return;
    }

    localStorage.setItem('backUrl', '/dashboard');
    void this.router.navigateByUrl('/dashboard');
  }

  private clearMasqueradeStorage(): void {
    localStorage.removeItem('masqueradeActive');
    localStorage.removeItem('masqueradeAdminToken');
    localStorage.removeItem('masqueradeAdminName');
    localStorage.removeItem('masqueradeAdminRole');
    localStorage.removeItem('masqueradeUsername');
  }

  private getDisplayName(user: any): string {
    const fullName = `${user?.fname || ''} ${user?.lname || ''}`.trim();
    return fullName || user?.screenName || user?.username || user?.email || 'User';
  }
  onJoinSelectedChallenges(): void {
    if (this.selectedChallengesCount <= 0) return;
    this.userHeaderUiStateService.triggerAddChallengeJoin();
  }

  goToTeamsAdminTab(event?: Event): void {
    event?.preventDefault();
    const fromProfile = this.userHeaderUiStateService.isAddChallengeFromProfile();

    if (fromProfile) {
      this.userHeaderUiStateService.triggerAddChallengeProfileBack();
      this.userHeaderUiStateService.setAddChallengeActive(false);
      void this.router.navigate(['/user/user-profile'], { queryParams: { tab: 'teams' } });
      return;
    }

    this.userHeaderUiStateService.setAddChallengeActive(false);
    void this.router.navigate(['/user/user-teams'], { queryParams: { tab: 'admin' } });
  }
}